import { ApplicantAttachmentDto } from '@inosio/payments-sdk';
import { Alert, Box, CircularProgress, Grid, IconButton, Snackbar, Table, TableBody, TableCell, TableContainer, TableRow, Typography } from '@mui/material';
import NumberFormat from 'react-number-format';
import FilePdfIcon from '../../../../icons/FilePdfIcon.svg';
import MountainIcon from '../../../../icons/Mountain.svg';
import SearchIcon from '@mui/icons-material/Search';
import { downloadApplicantAttachment, fetchApplicantAttachmentUrl, selectApplicationsState } from '../../../../resui/applications/sagas/pm/applicationsSlice';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import DownloadBlack from '../../../../icons/DownloadBlack.svg';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import EditPinIcon from '../../../../icons/EditPinIcon.svg';

export enum ApplicantEntityDataTableFieldType {
  MinorUnitsNumber = 'MinorUnitsNumber',
  Hidden = 'Hidden',
}
export interface ApplicantEntityDataTableField {
  /**
   *The label of the field in the table
   */
  label: string;

  /**
   * The value of the field in the table
   */
  value: string | number;

  /**
   * Optional prop useful for indentation to show parent and child fields
   */
  paddingLeft?: string;

  /**
   * Optional prop to specify the type of field (minor units, masked etc)
   */
  fieldType?: ApplicantEntityDataTableFieldType;

  /**
   * Optional prop to tell if the field is editable. An edit icon would appear right next to the field
   */
  editable?: boolean;

  /**
   * Optional callback fired when the edit icon on the field is clicked
   */
  onEdit?: () => void;
}

export interface ApplicationDetailsApplicantEntityDataProps {
  tableData: ApplicantEntityDataTableField[];
  attachments?: ApplicantAttachmentDto[];
}
export default function ApplicationDetailsApplicantEntityData(props: ApplicationDetailsApplicantEntityDataProps) {
  const dispatch = useDispatch();
  const {
    isFetchingApplicantAttachmentUrlSuccess,
    applicantAttachmentUrl,
    isFetchingApplicantAttachmentUrl,
    isDownloadingApplicantAttachment,
    isDownloadingApplicantAttachmentFailed,
    isFetchingApplicantAttachmentUrlFailed,
    isDownloadingApplicantAttachmentSuccess,
  } = useSelector(selectApplicationsState);
  // Add state to track the selected attachment
  const [selectedAttachmentUuid, setSelectedAttachmentUuid] = useState<string | null>(null);
  const [showHiddenField, setShowHiddenField] = useState(false);
  const [snackBarOpen, setSnackBarOpen] = useState(false);
  const [localError, setLocalError] = useState('');

  const handleAlertClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackBarOpen(false);
  };

  const handleToggleVisibility = () => {
    setShowHiddenField(!showHiddenField);
  };

  //open the tab only for the selected attachment
  useEffect(() => {
    if (isFetchingApplicantAttachmentUrlSuccess && applicantAttachmentUrl && selectedAttachmentUuid) {
      window.open(applicantAttachmentUrl);
      setSelectedAttachmentUuid(null); // reset the selected attachment after opening the URL
    }
  }, [isFetchingApplicantAttachmentUrlSuccess, applicantAttachmentUrl, selectedAttachmentUuid]);

  const handleDownloadAllAttachments = () => {
    if (!!props.attachments?.length) {
      props.attachments.forEach((attachment) => {
        dispatch({
          type: downloadApplicantAttachment.type,
          payload: {
            remoteUuid: attachment.RemoteUuid,
            fileType: attachment.FileMimeType,
          },
        });
      });
    }
  };

  useEffect(() => {
    if (isDownloadingApplicantAttachmentSuccess && isFetchingApplicantAttachmentUrlSuccess) {
      setLocalError('');
      setSnackBarOpen(false);
    } else if (isDownloadingApplicantAttachmentFailed || isFetchingApplicantAttachmentUrlFailed) {
      setSnackBarOpen(true);
      setLocalError(`Apologies, the request to download the attachment failed. Please try again later or contact support. Thank you.`);
    }
  }, [isDownloadingApplicantAttachmentFailed, isFetchingApplicantAttachmentUrlFailed, isFetchingApplicantAttachmentUrlSuccess, isDownloadingApplicantAttachmentSuccess]);

  return (
    <Grid container spacing={2} mb={2}>
      <Snackbar anchorOrigin={{ horizontal: 'center', vertical: 'top' }} open={snackBarOpen} autoHideDuration={4000} onClose={handleAlertClose}>
        <Alert onClose={handleAlertClose} severity="error">
          {localError}
        </Alert>
      </Snackbar>
      <Grid item xs={12} md={props.attachments ? 9 : 12}>
        <TableContainer>
          <Table size="small">
            <TableBody>
              {props.tableData.map((row, index) => (
                <TableRow key={index}>
                  <TableCell style={{ fontWeight: 'bold', width: '30%', verticalAlign: 'top', border: 'none' }}>
                    <Typography sx={{ fontFamily: 'Work Sans !important', fontWeight: 'bold', paddingLeft: row.paddingLeft }}>{row.label}:</Typography>
                  </TableCell>
                  <TableCell style={{ width: '70%', verticalAlign: 'top', border: 'none' }}>
                    {typeof row.value === 'number' ? (
                      <Typography sx={{ fontFamily: 'Work Sans !important' }}>
                        <NumberFormat
                          thousandsGroupStyle="thousand"
                          value={row.fieldType === ApplicantEntityDataTableFieldType.MinorUnitsNumber ? row.value / 100 : row.value}
                          prefix="$"
                          decimalSeparator="."
                          displayType="text"
                          type="text"
                          thousandSeparator={true}
                          allowNegative={true}
                          decimalScale={0}
                          fixedDecimalScale={true}
                          style={{ fontFamily: 'Work Sans !important' }}
                        />
                      </Typography>
                    ) : row.fieldType === ApplicantEntityDataTableFieldType.Hidden && !!row.value ? (
                      row.value !== '(left blank)' ? (
                        <Typography sx={{ fontFamily: 'Work Sans !important', display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '170px' }}>
                          {showHiddenField ? row.value : '***-**-****'}
                          <IconButton size="small" onClick={handleToggleVisibility}>
                            {showHiddenField ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </Typography>
                      ) : (
                        <Typography sx={{ fontFamily: 'Work Sans !important' }}>{row.value}</Typography>
                      )
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography sx={{ fontFamily: 'Work Sans !important' }}>{row.value}</Typography>
                        {row.editable && <img src={EditPinIcon} alt="Edit property" style={{ cursor: 'pointer', width: 13, height: 17, marginLeft: '10px' }} onClick={row.onEdit} />}
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
      {props.attachments && (
        <Grid item xs={12} md={3}>
          <Typography sx={{ fontFamily: 'Work Sans !important', fontWeight: 'bold', marginBottom: 1 }}>Attachments:</Typography>
          {!!props.attachments?.length ? (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{ cursor: 'pointer' }} onClick={handleDownloadAllAttachments}>
                  <Typography sx={{ fontFamily: 'Work Sans !important' }}>Download All</Typography>
                </Box>
                <img src={DownloadBlack} alt="download all" width={13} height={14} style={{ marginLeft: 20, cursor: 'pointer' }} onClick={handleDownloadAllAttachments} />
              </Box>
              <Grid container columnGap={3} rowGap={3}>
                {props.attachments?.map((attachment) => (
                  <Grid
                    item
                    key={attachment.RemoteUuid}
                    sx={{
                      height: '81px',
                      width: '81px',
                      backgroundColor: '#D3D3D3',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      cursor: 'pointer',
                      borderRadius: '9px',
                      position: 'relative',
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0)',
                        borderRadius: '9px',
                        transition: 'background-color 0.2s ease',
                      },
                      '&:hover::after': {
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                      },
                      '&:hover .searchIcon': {
                        opacity: 1,
                      },
                      '&:hover .spinner': {
                        color: 'white', // Change spinner color to white on hover of the Grid for better aestheitcs
                      },
                    }}
                    onClick={() => {
                      setSelectedAttachmentUuid(attachment.RemoteUuid);
                      dispatch({
                        type: fetchApplicantAttachmentUrl.type,
                        payload: {
                          remoteUuid: attachment.RemoteUuid,
                          fileType: attachment.FileMimeType,
                        },
                      });
                    }}
                  >
                    <img src={attachment.FileMimeType?.includes('pdf') ? FilePdfIcon : MountainIcon} style={{ color: 'red', height: '35px', width: '35px' }} alt="FilePdfIcon" />
                    {(isFetchingApplicantAttachmentUrl && selectedAttachmentUuid === attachment.RemoteUuid) || isDownloadingApplicantAttachment ? (
                      <CircularProgress
                        className="spinner"
                        sx={{
                          position: 'absolute',
                          zIndex: 1,
                        }}
                        size={35}
                      />
                    ) : (
                      <SearchIcon
                        className="searchIcon"
                        sx={{
                          position: 'absolute',
                          opacity: 0, // Initially hidden
                          color: 'white',
                          fontSize: '35px',
                          zIndex: 1, // Ensure the icon is above the overlay
                          transition: 'opacity 0.2s ease',
                        }}
                      />
                    )}
                  </Grid>
                ))}
              </Grid>
            </Box>
          ) : (
            <Typography sx={{ fontFamily: 'Work Sans !important', marginBottom: 1 }}>No attachments were provided.</Typography>
          )}
        </Grid>
      )}
    </Grid>
  );
}
